import { documentObjectIconMap } from "Common/dictionaries/documentObjectIconId";
import { OPTION_TYPE } from "Common/dictionaries/optionType";
import { TableLayoutTabItemId } from "devexpress-richedit";
import { TableWidthUnitType } from "devexpress-richedit/lib/core/model/tables/secondary-structures/table-units";
import filter from "lodash/filter";
import get from "lodash/get";
import map from "lodash/map";
import toString from "lodash/toString";
import uniq from "lodash/uniq";
import locale from "./addToReportTableRows.locale";
import { defaultCharacterProperties, pageSubHeaderCharacterProperties } from "./characterProperties";
import getImageBase64 from "./getImageBase64";
const headerCharacterProperties = {
    ...defaultCharacterProperties,
    bold: true
};
const SIZES = {
    LEFT_INDENT: 150,
    RIGHT_INDENT: 150,
    SPACING_BEFORE: 80,
    SPACING_AFTER: 100,
    SPACING_BEFORE_HEADER: 50,
    SPACING_AFTER_HEADER: 50,
    ICON_SIZE: 26,
    IMAGE_SIZE: 60
};
export default async (richRef, documentObjects, title, headers, rows, progressRef, abortControllerRef, processResolve) => {
    var _a;
    if (!richRef.current) {
        processResolve(false);
        return;
    }
    let hasTransaction = false;
    const documentObjectIds = uniq(map(rows, "DocumentObjectId"));
    const hasRowsWithNodeSources = Boolean(documentObjectIds.length);
    const imageColumnIndexes = filter(map(headers, (f, ind) => (f.Type === OPTION_TYPE.IMAGE ? ind - 1 : -1)), i => i > -1);
    (_a = progressRef.current) === null || _a === void 0 ? void 0 : _a.setMessage(locale.PREPARING_IMAGES, async () => {
        var _a, _b, _c;
        if (!richRef.current) {
            processResolve(false);
            return;
        }
        const base64DefaultImagesMap = {};
        await Promise.all(map(uniq(map(documentObjectIds, documentObjectId => { var _a; return (_a = documentObjects[documentObjectId]) === null || _a === void 0 ? void 0 : _a.IconId; })), async (iconId) => {
            var _a;
            if (!iconId)
                return;
            if ((_a = abortControllerRef.current) === null || _a === void 0 ? void 0 : _a.signal.aborted)
                return;
            const imgSrc = documentObjectIconMap[iconId];
            const base64 = await getImageBase64(imgSrc, SIZES.ICON_SIZE, undefined, abortControllerRef.current);
            base64DefaultImagesMap[iconId] = base64;
        }));
        if ((_a = abortControllerRef.current) === null || _a === void 0 ? void 0 : _a.signal.aborted) {
            processResolve(false);
            return;
        }
        const initialPosition = richRef.current.selection.active;
        const endUpdate = (error, needProcessResolve) => {
            var _a;
            if (!hasTransaction)
                return;
            (_a = progressRef.current) === null || _a === void 0 ? void 0 : _a.setMessage(locale.PROCESS_COMPLETION, async () => {
                if (!richRef.current)
                    return;
                needProcessResolve &&
                    richRef.current.selection.setSelection({ start: initialPosition, length: 0 });
                richRef.current.history.endTransaction();
                richRef.current.endUpdate();
                needProcessResolve && processResolve(!error);
                hasTransaction = false;
            });
        };
        richRef.current.beginUpdate();
        richRef.current.history.beginTransaction();
        hasTransaction = true;
        const subDocument = richRef.current.selection.activeSubDocument;
        let position = initialPosition;
        subDocument.insertParagraph(position);
        subDocument.setCharacterProperties({ start: position, length: 1 }, defaultCharacterProperties);
        subDocument.insertText(position, title);
        subDocument.setCharacterProperties({ start: position, length: title.length }, pageSubHeaderCharacterProperties);
        position += title.length;
        subDocument.insertParagraph(position);
        subDocument.insertParagraph(position);
        subDocument.setCharacterProperties({ start: position, length: 2 }, defaultCharacterProperties);
        position += 2;
        if ((_b = abortControllerRef.current) === null || _b === void 0 ? void 0 : _b.signal.aborted) {
            endUpdate(true, true);
            return;
        }
        endUpdate(false, false);
        const colsCount = headers.length + (hasRowsWithNodeSources ? 1 : 0);
        (_c = progressRef.current) === null || _c === void 0 ? void 0 : _c.setMessage(locale.buildCreatingTable(colsCount, rows.length), async () => {
            var _a, _b, _c, _d;
            if (!richRef.current) {
                processResolve(false);
                return;
            }
            richRef.current.beginUpdate();
            richRef.current.history.beginTransaction();
            hasTransaction = true;
            const tableApi = subDocument.tables.create(position, colsCount, rows.length + 1);
            richRef.current.selection.setSelection(tableApi.interval);
            (_a = richRef.current) === null || _a === void 0 ? void 0 : _a.executeCommand(TableLayoutTabItemId.SetAutoFitWindow);
            const table = get(tableApi, "_table");
            const headerRow = table === null || table === void 0 ? void 0 : table.rows.at(0);
            if (!table || !headerRow || ((_b = abortControllerRef.current) === null || _b === void 0 ? void 0 : _b.signal.aborted)) {
                endUpdate(true, true);
                return;
            }
            if (hasRowsWithNodeSources) {
                const imageCell = headerRow.cells.at(0);
                if (imageCell) {
                    imageCell.preferredWidth.type = TableWidthUnitType.ModelUnits;
                    imageCell.preferredWidth.value = richRef.current.unitConverter.pixelsToTwips(SIZES.IMAGE_SIZE);
                }
            }
            headers.every((header, ind) => {
                var _a;
                if ((_a = abortControllerRef.current) === null || _a === void 0 ? void 0 : _a.signal.aborted)
                    return false;
                const cell = headerRow.cells.at(ind + (hasRowsWithNodeSources ? 1 : 0));
                if (!cell)
                    return false;
                subDocument.insertText(cell.interval.start, header.Name);
                subDocument.setCharacterProperties({ start: cell.interval.start, length: header.Name.length }, headerCharacterProperties);
                subDocument.setParagraphProperties(cell.interval, {
                    leftIndent: SIZES.LEFT_INDENT,
                    rightIndent: SIZES.RIGHT_INDENT,
                    spacingBefore: SIZES.SPACING_BEFORE_HEADER,
                    spacingAfter: SIZES.SPACING_AFTER_HEADER
                });
                return true;
            });
            if ((_c = abortControllerRef.current) === null || _c === void 0 ? void 0 : _c.signal.aborted) {
                endUpdate(true, true);
                return;
            }
            endUpdate(false, false);
            richRef.current.beginUpdate();
            richRef.current.history.beginTransaction();
            hasTransaction = true;
            let rowsInsertedCount = 0;
            await Promise.all(map(rows, async (item, ind) => new Promise(resolve => {
                setTimeout(async () => {
                    var _a, _b, _c, _d, _e;
                    if (!richRef.current ||
                        richRef.current.isDisposed ||
                        ((_a = abortControllerRef.current) === null || _a === void 0 ? void 0 : _a.signal.aborted)) {
                        resolve(rowsInsertedCount);
                        processResolve(false);
                        return;
                    }
                    const row = table === null || table === void 0 ? void 0 : table.rows.at(ind + 1);
                    if (!row || row.cells.length !== colsCount) {
                        resolve(rowsInsertedCount);
                        return;
                    }
                    const rowBase64 = {};
                    await Promise.all(map(imageColumnIndexes, async (imageColumnIndex) => {
                        var _a;
                        if ((_a = abortControllerRef.current) === null || _a === void 0 ? void 0 : _a.signal.aborted)
                            return;
                        const imgCellValue = get(item.Values, imageColumnIndex);
                        if (!imgCellValue)
                            return;
                        rowBase64[imageColumnIndex] = await getImageBase64(imgCellValue, SIZES.IMAGE_SIZE, "border-rounded-circle", abortControllerRef.current);
                    }));
                    let cell = row.cells.at(0);
                    if (!cell) {
                        resolve(rowsInsertedCount);
                        return;
                    }
                    if (hasRowsWithNodeSources) {
                        const iconId = item.DocumentObjectId
                            ? documentObjects[item.DocumentObjectId].IconId
                            : undefined;
                        const documentObjectImage = iconId ? base64DefaultImagesMap[iconId] : undefined;
                        if (documentObjectImage) {
                            subDocument.insertPicture(cell.interval.start, documentObjectImage);
                            subDocument.setParagraphProperties(cell.interval, {
                                leftIndent: SIZES.LEFT_INDENT,
                                rightIndent: SIZES.RIGHT_INDENT,
                                spacingBefore: SIZES.SPACING_BEFORE,
                                spacingAfter: SIZES.SPACING_AFTER
                            });
                        }
                        cell = row.cells.at(1);
                        if (!cell) {
                            resolve(rowsInsertedCount);
                            return;
                        }
                    }
                    if ((_b = abortControllerRef.current) === null || _b === void 0 ? void 0 : _b.signal.aborted) {
                        resolve(rowsInsertedCount);
                        processResolve(false);
                        return;
                    }
                    const documentObjectName = (_c = documentObjects[item.DocumentObjectId]) === null || _c === void 0 ? void 0 : _c.Name;
                    if (documentObjectName) {
                        subDocument.insertText(cell.interval.start, documentObjectName);
                        subDocument.setCharacterProperties(cell.interval, defaultCharacterProperties);
                        subDocument.setParagraphProperties(cell.interval, {
                            leftIndent: SIZES.LEFT_INDENT,
                            rightIndent: SIZES.RIGHT_INDENT,
                            spacingBefore: SIZES.SPACING_BEFORE,
                            spacingAfter: SIZES.SPACING_AFTER
                        });
                    }
                    if ((_d = abortControllerRef.current) === null || _d === void 0 ? void 0 : _d.signal.aborted) {
                        resolve(rowsInsertedCount);
                        processResolve(false);
                        return;
                    }
                    item.Values.every((value, valInd) => {
                        var _a;
                        cell = row.cells.at(valInd + (hasRowsWithNodeSources ? 2 : 1));
                        if (!cell)
                            return false;
                        if ((_a = abortControllerRef.current) === null || _a === void 0 ? void 0 : _a.signal.aborted) {
                            resolve(rowsInsertedCount);
                            processResolve(false);
                            return false;
                        }
                        const cellBase64 = rowBase64[valInd];
                        if (cellBase64) {
                            subDocument.insertPicture(cell.interval.start, cellBase64);
                        }
                        else {
                            subDocument.insertText(cell.interval.start, toString(value));
                            subDocument.setCharacterProperties(cell.interval, defaultCharacterProperties);
                        }
                        subDocument.setParagraphProperties(cell.interval, {
                            leftIndent: SIZES.LEFT_INDENT,
                            rightIndent: SIZES.RIGHT_INDENT,
                            spacingBefore: SIZES.SPACING_BEFORE,
                            spacingAfter: SIZES.SPACING_AFTER
                        });
                        return true;
                    });
                    rowsInsertedCount += 1;
                    (_e = progressRef.current) === null || _e === void 0 ? void 0 : _e.setMessage(locale.buildRowsCountCompleted(rowsInsertedCount, rows.length));
                    resolve(rowsInsertedCount);
                });
            })));
            endUpdate(Boolean((_d = abortControllerRef.current) === null || _d === void 0 ? void 0 : _d.signal.aborted), true);
        });
    });
};
